// @flow
import * as React from 'react'
import is from 'mz-utils/is'
import { isNil, isSyntheticEvent } from '../../../utils'
import AdaptiveSelectField from '../AdaptiveSelectField'

// Types
import type { Props as SelectFieldProps } from '../AdaptiveSelectField'
import type { QueryChangeOptions } from '../ControlledSearchableSelectField'

export type Props = SelectFieldProps & {
  searchable?: boolean,
  searchableOptions: {
    query?: ?string,
    onQueryChange?: (?string, ?QueryChangeOptions) => void,
    filterOptions?: (string, Array<any>) => Array<any> | Promise<Array<any>>,
    requestOptions?: (string) => Array<any> | Promise<Array<any>>,
    transformOptions?: (Array<any>, string) => Array<any>,
    renderEmpty?: (string) => any,
    allowSelectUnknown?: boolean,
    maxOptionsLength?: number,
    requestOptionsOnMount?: boolean,
    rememberInitialOptions?: boolean,
  },
}
type State = { focusedQuery: string }

/**
 * Select field wrapper which adds ability to search in the options
 * (combobox). It do not have any search logic, just renders what
 * is passed in the props (in `searchable` prop)
 */
class SearchableSelectField extends React.Component<Props, State> {
  state = { focusedQuery: '' }

  static defaultProps = {
    ...AdaptiveSelectField.defaultProps,
    searchableOptions: {},
  }

  UNSAFE_componentWillMount() {
    if (!Object.prototype.hasOwnProperty.call(this.props, 'emptyValue')) {
      throw new Error(
        'You have to proivide "emptyValue" prop to ' +
          'make searchable select to work.'
      )
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props, nextState: State) {
    if (
      nextProps.input.value !== this.props.input.value &&
      (nextProps.searchableOptions.query || nextState.focusedQuery)
    ) {
      this.handleChangeQuery(null, { loading: false })
    }
  }

  isQueryActive() {
    const { emptyValue } = this.props
    const { value } = this.props.input
    return (
      isNil(value, emptyValue) ||
      !isNil(this.state.focusedQuery) ||
      is.notUndef(this.props.searchableOptions.query)
    )
  }

  getInputValue() {
    const query =
      this.state.focusedQuery || this.props.searchableOptions.query || ''
    const { value } = this.props.input
    return this.isQueryActive() ? query : value
  }

  filterTargetProps = (targetProps: Object) => ({
    onChange: (e: any) => {
      this.handleInputChange(e, -1)
      targetProps.onFocus && targetProps.onFocus(e)
    },
    onKeyDown: (e: any) => {
      targetProps.onKeyDown && targetProps.onKeyDown(e)
    },
    onFocus: (e: any) => {
      targetProps.onFocus && targetProps.onFocus(e)
      const { onFocus } = this.props.input
      onFocus && onFocus(e)
    },
    onBlur: (e: any) => {
      targetProps.onBlur && targetProps.onBlur(e)
      this.handleInputBlur()
    },
  })

  renderOption = (option: any, data: Object) => {
    const {
      renderOption,
      searchableOptions: { query },
    } = this.props
    return renderOption(option, { ...data, query })
  }

  renderValue = (value: any, data: Object) => {
    const {
      renderValue,
      renderOption,
      searchableOptions: { query },
    } = this.props
    const renderer: Function = this.isQueryActive()
      ? AdaptiveSelectField.defaultProps.renderOption
      : renderValue || renderOption

    return renderer(value, { ...data, query }) || ''
  }

  handleChangeQuery(
    queryValue: ?string,
    queryChangeOptions: ?QueryChangeOptions
  ) {
    const { onQueryChange } = this.props.searchableOptions
    this.setState({ focusedQuery: '' })
    onQueryChange && onQueryChange(queryValue, queryChangeOptions)
  }

  handleChangeValue(newValue: any, index: number) {
    const {
      input: { value: currValue, onChange },
    } = this.props

    // in redux-form null is replaced to empty string by default
    // unless you are using format={null} for Field
    // TODO: remove it later
    const isValueNotReplacedToEmptyByReduxForm = !(
      newValue === null && currValue === ''
    )

    if (
      newValue !== currValue &&
      isValueNotReplacedToEmptyByReduxForm &&
      onChange
    ) {
      onChange(newValue, index)
    }
  }

  handleOptionFocus = (option: any) => {
    const { renderOption, renderValue } = this.props
    const renderer = renderValue || renderOption
    this.setState({ focusedQuery: renderer(option, {}) })
  }

  handleInputChange = (event: any, index: number) => {
    const eventValue = isSyntheticEvent(event) ? event.target.value : event

    if (is.number(index) && index >= 0) {
      this.handleChangeQuery(null, { loading: false, keepOptions: true })
      this.handleChangeValue(eventValue, index)
    } else {
      this.handleChangeQuery(eventValue)
    }
  }

  handleInputBlur = () => {
    // Passing 'undefined' instead of actual event to
    // fix redux-form behaviour when it set query from input
    // as a value, which is absolutely not needed
    const { onBlur } = this.props.input
    onBlur && onBlur(undefined)

    // Remove query and empty value on blur of the fieled
    const {
      emptyValue,
      searchableOptions: { query },
    } = this.props
    if (is.string(query)) {
      this.handleChangeQuery(null, { loading: false })
      this.handleChangeValue(emptyValue, -1)
    }
  }

  handleCloseMobileSelector = () => {
    this.handleChangeQuery(null, { loading: false, keepOptions: true })
  }

  handleClickClear = () => {
    this.handleChangeQuery(null, { loading: false })
    this.handleChangeValue(this.props.emptyValue, -1)
  }

  handleHidePopper = () => {
    this.setState({ focusedQuery: '' })
  }

  render() {
    const { searchableOptions, searchable, ...restProps } = this.props
    return (
      <AdaptiveSelectField
        {...restProps}
        combobox={true}
        onClickClear={this.handleClickClear}
        filterTargetProps={this.filterTargetProps}
        onFocusOption={this.handleOptionFocus}
        onHidePopper={this.handleHidePopper}
        onCloseMobileSelector={this.handleCloseMobileSelector}
        renderValue={this.renderValue}
        renderOption={this.renderOption}
        input={{
          ...this.props.input,
          onBlur: this.handleInputBlur,
          onChange: this.handleInputChange,
          value: this.getInputValue(),
        }}
      />
    )
  }
}

export default SearchableSelectField
